import {
  U21Chip,
  U21MenuLayout,
  U21Spacer,
} from 'app/shared/u21-ui/components';
import { useState } from 'react';
import { EvaluationFlag } from 'app/modules/detectionModels/components/ConditionBreakdown/Operand';
import { IconChevronDown } from '@u21/tabler-icons';
import { ConditionCard } from 'app/modules/detectionModels/components/ConditionBreakdown/ConditionCard';
import {
  ConditionValue,
  getFriendlyOperator,
} from 'app/modules/detectionModels/components/ConditionBreakdown/ConditionValue';
import {
  CompositeTypes,
  DmOperator,
  TagValue,
  ValueTypes,
} from 'app/modules/rulesAdvanced/models';
import { ConditionalOperators } from 'app/modules/detectionModels/constants';
import { EnrichedQueryTreeNode } from 'app/modules/rules/types/responses';
import styled from 'styled-components';

const getFriendlyTagText = (tagOperator: TagValue) => {
  let friendlyType = '';

  if (tagOperator.tag_type === 'EXCLUSION') {
    friendlyType = 'Excludes';
  } else if (tagOperator.tag_type === 'INCLUSION') {
    friendlyType = 'Includes';
  }

  return `${friendlyType} ${tagOperator.tag_operator === 'OR' ? 'any' : 'all'}`;
};

const getVariables = (op: EnrichedQueryTreeNode) => {
  const result: any[] = [];

  function traverse(current) {
    if (!current) return;

    if (Array.isArray(current)) {
      current.forEach(traverse);
    } else if (typeof current === 'object') {
      // If it's a VARIABLE operand, add it to the result
      if (current.type === ValueTypes.VARIABLE || current.type === 'fact_id') {
        result.push(current);
      }

      // If it's a composite with operands, recurse into them
      if (current.operands && Array.isArray(current.operands)) {
        traverse(current.operands);
      }
    }
  }

  traverse(op);
  return result;
};

interface ConditionProps {
  op: EnrichedQueryTreeNode;
  swapWithSnapshotValue?: boolean;
  showMenu?: boolean;
}

const handleOperand = (
  operator: ConditionalOperators | DmOperator,
  elements,
) => {
  if (operator === ConditionalOperators.BETWEEN) {
    elements.splice(2, 0, <div> and </div>);
  }
  return elements.slice(1);
};

export const Condition = ({
  op,
  swapWithSnapshotValue = false,
  showMenu = true,
}: ConditionProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  if (op === null) {
    return null;
  }

  if (op.type === ValueTypes.TAG) {
    return (
      <U21Spacer horizontal>
        {!swapWithSnapshotValue && (
          <EvaluationFlag evaluationResult={op.evaluation_result} />
        )}
        <span>
          {' '}
          {getFriendlyTagText(op)}{' '}
          {!swapWithSnapshotValue ? (
            <U21Chip>{op.tags_fields.length} tags</U21Chip>
          ) : (
            <U21Spacer wrap>
              {op.tags_fields.map((tag) => (
                <U21Chip key={tag.tag_name}>{tag.tag_name}</U21Chip>
              ))}
            </U21Spacer>
          )}
        </span>
        {showMenu && (
          <U21MenuLayout
            onClose={() => {
              setIsOpen(false);
            }}
            open={isOpen}
            trigger={
              <U21Chip
                onClick={() => {
                  setIsOpen(true);
                }}
                variant="outlined"
                endIcon={<IconChevronDown />}
              >
                Cannot be displayed
              </U21Chip>
            }
          >
            <ConditionCard op={op} isUnsupportedTags />
          </U21MenuLayout>
        )}
      </U21Spacer>
    );
  }

  if (
    op.type !== CompositeTypes.RULE_CONDITION_COMPOSITE &&
    op.type !== CompositeTypes.COMPOSITE
  ) {
    return null;
  }

  const el = op.operands.map((ope, index) => (
    <ConditionValue
      // eslint-disable-next-line react/no-array-index-key
      key={index}
      value={ope}
      swapWithSnapshotValue={swapWithSnapshotValue}
    />
  ));

  const variables = getVariables(op);

  if (!variables || variables.length === 0) {
    return null;
  }

  const displayValue =
    variables.length === 1 ? `${variables[0].snapshot_value}` : `Show more`;

  return (
    <U21Spacer horizontal>
      {!swapWithSnapshotValue && (
        <EvaluationFlag evaluationResult={op.evaluation_result} />
      )}
      {el[0]} <span>{getFriendlyOperator(op.operator)}</span>
      {handleOperand(op.operator, el)}
      {showMenu && (
        <U21MenuLayout
          onClose={() => {
            setIsOpen(false);
          }}
          open={isOpen}
          trigger={
            <StyledU21Chip
              onClick={() => {
                setIsOpen(true);
              }}
              variant="outlined"
              endIcon={<IconChevronDown />}
            >
              Value: {displayValue}
            </StyledU21Chip>
          }
        >
          <ConditionCard op={op} variables={variables} />
        </U21MenuLayout>
      )}
    </U21Spacer>
  );
};

const StyledU21Chip = styled(U21Chip)`
  padding-right: 2px;
`;
